if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.colorChangeTimer = null;

        if (!this.elements.viewer) return;

        this.productSection =
          this.closest('.shopify-section') ||
          this.closest('[id^="MainProduct-"]') ||
          document;

        /*
         * Run a second media update after Shopify finishes changing variants.
         * This prevents Pink's image from remaining active after Blue is
         * selected again.
         */
        this.onColorChange = this.onColorChange.bind(this);

        this.productSection.addEventListener(
          'change',
          this.onColorChange
        );

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );

          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');

              if (!button) return;

              button.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            });

          if (
            this.dataset.desktopLayout?.includes('thumbnail') &&
            this.mql.matches
          ) {
            this.removeListSemantic();
          }
        }
      }

      onColorChange(event) {
        const selectedValue = String(
          event.target?.value || ''
        )
          .trim()
          .toLowerCase();

        if (
          selectedValue !== 'blue' &&
          selectedValue !== 'pink'
        ) {
          return;
        }

        window.clearTimeout(this.colorChangeTimer);

        /*
         * Wait until Shopify and the bundle selector have finished updating
         * the selected variant before activating the correct visible image.
         */
        this.colorChangeTimer = window.setTimeout(() => {
          this.activateColorMedia(selectedValue);
        }, 200);
      }

      activateColorMedia(color) {
        if (!this.elements.viewer) return;

        let targetAltText = null;

        if (color === 'blue') {
          targetAltText = 'blue-hero';
        }

        if (color === 'pink') {
          targetAltText = 'pink-gallery';
        }

        if (!targetAltText) return;

        const targetImage = this.elements.viewer.querySelector(
          `img[alt="${targetAltText}"]`
        );

        const targetMedia = targetImage?.closest(
          '[data-media-id]'
        );

        if (!targetMedia?.dataset?.mediaId) return;

        /*
         * False keeps the images in their original positions:
         * Blue stays second-to-last and Pink stays last.
         */
        this.setActiveMedia(
          targetMedia.dataset.mediaId,
          false
        );
      }

      getSelectedColor() {
        const selectedColorInput = Array.from(
          this.productSection.querySelectorAll(
            'input[type="radio"]:checked'
          )
        ).find((input) => {
          const value = String(input.value)
            .trim()
            .toLowerCase();

          return value === 'blue' || value === 'pink';
        });

        if (selectedColorInput) {
          return String(selectedColorInput.value)
            .trim()
            .toLowerCase();
        }

        const selectedColorDropdown = Array.from(
          this.productSection.querySelectorAll('select')
        ).find((select) => {
          const value = String(select.value)
            .trim()
            .toLowerCase();

          return value === 'blue' || value === 'pink';
        });

        if (selectedColorDropdown) {
          return String(selectedColorDropdown.value)
            .trim()
            .toLowerCase();
        }

        return '';
      }

      onSlideChanged(event) {
        if (
          !this.elements.thumbnails ||
          !event.detail?.currentElement?.dataset?.mediaId
        ) {
          return;
        }

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;

        /*
         * Check whether Shopify's requested image exists in the gallery.
         * bundle-hidden variant images are intentionally absent.
         */
        const requestedMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );

        let mappedVariantMedia = null;

        /*
         * When Shopify requests a hidden variant image, map the currently
         * selected color to its visible gallery image.
         */
        if (!requestedMedia) {
          const selectedColor = this.getSelectedColor();

          let targetAltText = null;

          if (selectedColor === 'blue') {
            targetAltText = 'blue-hero';
          }

          if (selectedColor === 'pink') {
            targetAltText = 'pink-gallery';
          }

          if (targetAltText) {
            const targetImage = this.elements.viewer.querySelector(
              `img[alt="${targetAltText}"]`
            );

            mappedVariantMedia = targetImage?.closest(
              '[data-media-id]'
            );
          }
        }

        const currentActiveMedia = this.elements.viewer.querySelector(
          '[data-media-id].is-active'
        );

        const firstVisibleMedia = this.elements.viewer.querySelector(
          '[data-media-id]'
        );

        const activeMedia =
          requestedMedia ||
          mappedVariantMedia ||
          currentActiveMedia ||
          firstVisibleMedia;

        if (!activeMedia) return;

        const activeMediaId = activeMedia.dataset.mediaId;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        let activeThumbnail = null;

        if (
          this.elements.thumbnails &&
          activeMediaId
        ) {
          activeThumbnail =
            this.elements.thumbnails.querySelector(
              `[data-target="${activeMediaId}"]`
            );
        }

        /*
         * Do not reorder Blue or Pink. They remain in their original gallery
         * positions and only become active when their color is selected.
         */
        const requestedAltText = requestedMedia
          ?.querySelector('img')
          ?.getAttribute('alt')
          ?.trim()
          .toLowerCase();

        if (
          prepend &&
          requestedMedia &&
          requestedAltText !== 'blue-hero' &&
          requestedAltText !== 'pink-gallery'
        ) {
          if (
            activeMedia.parentElement &&
            activeMedia.parentElement.firstElementChild !==
              activeMedia
          ) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (
            activeThumbnail &&
            activeThumbnail.parentElement &&
            activeThumbnail.parentElement.firstElementChild !==
              activeThumbnail
          ) {
            activeThumbnail.parentElement.prepend(
              activeThumbnail
            );
          }

          if (
            this.elements.viewer.slider &&
            typeof this.elements.viewer.resetPages ===
              'function'
          ) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (activeMedia.parentElement) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'smooth',
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top + window.scrollY;

          window.scrollTo({
            top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail?.dataset?.mediaPosition) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        const button = thumbnail.querySelector('button');

        if (button) {
          button.setAttribute(
            'aria-current',
            'true'
          );
        }

        if (
          typeof this.elements.thumbnails.isSlideVisible ===
            'function' &&
          this.elements.thumbnails.isSlideVisible(
            thumbnail,
            10
          )
        ) {
          return;
        }

        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
            behavior: 'smooth',
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        if (
          !activeItem ||
          !position ||
          !this.elements.liveRegion ||
          !window.accessibilityStrings?.imageAvailable
        ) {
          return;
        }

        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            'false'
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          window.setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              'true'
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;

        if (
          typeof window.pauseAllMedia === 'function'
        ) {
          window.pauseAllMedia();
        }

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (
          !this.elements.viewer?.slider ||
          !this.elements.viewer.sliderItems
        ) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          }
        );
      }
    }
  );
}